import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

export interface GeolocationState {
  position: GeolocationPosition | null;
  error: string | null;
  isLoading: boolean;
  isWatching: boolean;
  accuracy: number | null;
}

export interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  watch?: boolean;
}

export const useGeolocation = (options: UseGeolocationOptions = {}) => {
  const {
    enableHighAccuracy = true,
    timeout = 15000,
    maximumAge = 300000, // 5 minutes
    watch = false
  } = options;

  const [state, setState] = useState<GeolocationState>({
    position: null,
    error: null,
    isLoading: false,
    isWatching: false,
    accuracy: null
  });

  const watchIdRef = useRef<number | null>(null);

  const geolocationOptions: PositionOptions = useMemo(() => ({
    enableHighAccuracy,
    timeout,
    maximumAge
  }), [enableHighAccuracy, timeout, maximumAge]);

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    setState((prev) => ({
      ...prev,
      position,
      error: null,
      isLoading: false,
      accuracy: position.coords.accuracy
    }));
  }, []);

  const handleError = useCallback((error: GeolocationPositionError) => {
    let errorMessage = 'Location access failed';

    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location access denied. Please enable location permissions in your browser.';
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information unavailable. Please check your device settings.';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out. Please try again.';
        break;
      default:
        errorMessage = `Location error: ${error.message}`;
    }    setState((prev) => ({
      ...prev,
      error: errorMessage,
      isLoading: false,
      accuracy: null
    }));
  }, []);

  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by this browser.',
        isLoading: false
      }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      handleSuccess,
      handleError,
      geolocationOptions
    );
  }, [handleSuccess, handleError, geolocationOptions]);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by this browser.'
      }));
      return;
    }

    if (watchIdRef.current !== null) {
      return; // Already watching
    }

    setState((prev) => ({
      ...prev,
      isWatching: true,
      isLoading: true,
      error: null
    }));

    watchIdRef.current = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      geolocationOptions
    );
  }, [handleSuccess, handleError, geolocationOptions]);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    setState((prev) => ({
      ...prev,
      isWatching: false,
      isLoading: false
    }));
  }, []);

  const retry = useCallback(() => {
    if (state.isWatching) {
      stopWatching();
      setTimeout(startWatching, 100);
    } else {
      getCurrentPosition();
    }
  }, [state.isWatching, stopWatching, startWatching, getCurrentPosition]);

  // Auto-start watching if watch option is enabled
  useEffect(() => {
    if (watch) {
      startWatching();
    }

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, [watch, startWatching]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopWatching();
    };
  }, [stopWatching]);

  return {
    ...state,
    getCurrentPosition,
    startWatching,
    stopWatching,
    retry,
    isSupported: !!navigator.geolocation
  };
};

